import {useForm} from '@tanstack/react-form';
import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router';
import {NavLink, useLocation, useNavigate} from 'react-router-dom';
import {Breadcrumb} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {InputType} from '~/components/Controls/Input';
import {Trash2} from "lucide";
import {LucideIcon} from "~/components/LucideIcon";
import {tagListData, TagsList} from '~/components/Controls/Tags';
import {LinksList} from '~/components/Controls/LinksList';
import {useConfirm} from '~/components/Dialogs';
import {Progress} from '~/components/Progress';
import {BreadcrumbsResourcePage, ResourceBlock} from '~/components/ResourcePage';
import {TableRowActions, TableSort} from '~/components/Table';
import {Title} from '~/components/Title';
import {InfoPopover} from '~/components/InfoPopover';
import {useAbility} from '~/lib/ability';
import {useDataSource} from '~/lib/dataSource';
import {getApiErrorMessage} from '~/lib/errors/apiError';
import {Copy, renderBoolean, renderToggle} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {ResourceLoader, useQueryResource} from '~/lib/loader';
import {notify} from '~/lib/notify';
import {round2} from '~/lib/round2';
import {formatDateTime, utcDay} from '~/lib/date';
import {sortByBoolean, sortByNumber, sortByString} from '~/lib/sort';
import {
    CheckboxField,
    EditorField,
    InputField,
    SelectField,
    TagsField
} from '~/components/Form';
import {clientsLinks} from '~/modules/Clients/links';
import {biddersLinks} from '~/modules/Bidders';
import {BiddersAbility, PreTargetingAbility} from '~/modules/Bidders/ability';
import {preTargetingConfigsAdapter} from '~/modules/Bidders/adapters';
import {createBidder, fetchBidder, fetchBidderGroupsByBidder, fetchBidderSpendLimits, fetchCurrentHourSpend, fetchPreTargetingConfig, prepareBidderSubmitData, replanBidderSpendLimits, scaleBidderSpendLimits, updateBidder} from '~/modules/Bidders/api';
import {ReplanSpendLimitsModal, IReplanSpendLimitsModalProps} from '~/modules/Bidders/components/ReplanSpendLimitsModal';
import {ScaleSpendLimitsModal, IScaleSpendLimitsModalProps} from '~/modules/Bidders/components/ScaleSpendLimitsModal';
import {SelectBidderStatusField} from '~/modules/Bidders/components/SelectBidderStatus';
import {SelectFloorTypeField} from '~/modules/Bidders/components/SelectFloorType';
import {SelectBidderEncodingField} from '~/modules/Bidders/components/SelectBidderEncoding';
import {SelectBidderProtocolField} from '~/modules/Bidders/components/SelectBidderProtocol';
import {SelectDcField} from '~/modules/Bidders/components/SelectDc';
import {SelectAgencyField} from '~/modules/Clients/components/SelectAgency';
import {BidderFormCtx, Field, getFormBridgeErrors} from '~/modules/Bidders/forms/BidderFormBridge';
import {PreTargetingForm} from '~/modules/Bidders/forms/PreTargetingForm';
import {PreTargetingRulesEditorForm} from '~/modules/Bidders/forms/PreTargetingRulesEditorForm';
import {IPreTargetingFormActions} from '~/modules/Bidders/forms/PreTargetingForm';
import {
    defaultBidder,
    gpidRequirementItems,
    gpidRequirementLabels,
    IBidder,
    tagIdRequirementItems,
    tagIdRequirementLabels
} from '~/modules/Bidders/models/Bidder';
import {BarChart} from '~/modules/Dashboard/components/Charts';
import {Granularity, IPerformance} from '~/modules/Dashboard/models/Performance';
import {humanizePauseReason} from '~/modules/Bidders/models/BidderLite';
import {toBidderFormValues, toBidderSubmitValues} from '~/modules/Bidders/models/BidderFormMapper';
import {BidderFormValues, createBidderFormSchema} from '~/modules/Bidders/models/BidderFormSchema';
import {defaultPreTargeting, IPreTargetingConfig} from '~/modules/Bidders/models/PreTargeting';

import {IModalComponentClose, useModals} from '~/components/Modal';
import style from './index.module.scss';

interface IBidderViewParams extends Record<string, string | undefined> {
    bidderId?: string;
}

type InlineEditSection = 'general' | 'integration' | 'spendCapping' | 'mock';
type InlineEditMode = InlineEditSection | 'all';
type IPreTargetingModalProps = {
    preTargeting?: IPreTargetingConfig;
    onSubmit?: (values: IPreTargetingConfig, formActions: IPreTargetingFormActions, close: IModalComponentClose) => void;
};

const createRequiredFieldPriority: Array<keyof BidderFormValues> = ['title', 'endpoint', 'maxRpm', 'floorType'];

const focusInlineField = (field: string) => {
    if (typeof document === 'undefined') {
        return;
    }
    const elementById = document.getElementById(field) as HTMLElement | null;
    if (elementById && typeof (elementById as any).focus === 'function') {
        elementById.focus();
        return;
    }
    const container = document.querySelector(`.bidder-inline-focus-${field}`) as HTMLElement | null;
    const focusable = container?.querySelector<HTMLElement>('input, textarea, [tabindex="0"]');
    focusable?.focus();
};

const renderInfoFields = (fields: {label: string; value: React.ReactNode}[]) => fields.map((field, index) => {
    const value = field.value === null || field.value === undefined || field.value === ''
        ? '—'
        : field.value;
    return (
        <div key={`${field.label}-${index}`} className={style.infoItem}>
            <div className={style.infoLabel}>{field.label}</div>
            <div className={style.infoValue}>{value}</div>
        </div>
    );
});

const BidderCreateView = () => {
    const ability = useAbility(BiddersAbility);
    const confirm = useConfirm();
    const navigate = useNavigate();

    const bidderForm = useForm({
        defaultValues: toBidderFormValues(defaultBidder),
        validators: {
            onSubmit: createBidderFormSchema(ability.isAdmin()) as any
        },
        onSubmit: async ({value}) => {
            const bidder = toBidderSubmitValues(value);

            const save = async () => {
                try {
                    const createdBidder = await createBidder(prepareBidderSubmitData(bidder));
                    notify.success('Saved');
                    navigate(biddersLinks.bidder(createdBidder.id));
                } catch (error) {
                    notify.apiError(error);
                }
            };

            if (bidder.protocol === 'mock') {
                const approved = await new Promise<boolean>(resolve => {
                    confirm('Are you sure you want to save the bidder with mock protocol?', {
                        onOk: () => resolve(true),
                        onNo: () => resolve(false),
                        icon: <i className='fa fa-exclamation' />,
                        intent: Intent.DANGER
                    });
                });
                if (!approved) return;
            }

            await save();
        }
    });

    return (
        <Breadcrumb breadcrumb={{to: biddersLinks.bidders(), title: 'Bidders'}}>
            <bidderForm.Subscribe selector={state => state}>
                {state => {
                    const setFieldValue = (fieldName: string, fieldValue: unknown) => {
                        bidderForm.setFieldValue(fieldName as never, fieldValue as never);
                    };

                    const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
                        event?.preventDefault();
                        void bidderForm.handleSubmit();
                    };

                    const fieldErrors = getFormBridgeErrors(bidderForm);
                    const formBridge = {
                        errors: fieldErrors,
                        touched: {},
                        submitCount: state.submissionAttempts,
                        setFieldValue,
                        setStatus: (_: unknown) => undefined,
                        setSubmitting: (_submitting: boolean) => undefined,
                    };

                    const bidder = state.values as BidderFormValues;
                    const isSubmitting = state.isSubmitting;

                    const handleCreateClick = () => {
                        bidderForm.handleSubmit().then(() => {
                            const errors = getFormBridgeErrors(bidderForm);
                            const validationTarget = createRequiredFieldPriority.find(
                                field => Boolean((errors as any)?.[field])
                            ) ?? null;
                            if (!validationTarget) return;
                            focusInlineField(validationTarget);
                        });
                    };

                    return (
                        <BidderFormCtx.Provider value={{form: bidderForm, formBridge}}>
                        <BreadcrumbsResourcePage
                            title="Create bidder"
                            toolbar={
                                <>
                                    <Button
                                        intent={Intent.PRIMARY}
                                        responsive
                                        disabled={isSubmitting}
                                        leftIcon={<i className="fa fa-check" />}
                                        onClick={handleCreateClick}
                                    >
                                        Create
                                    </Button>
                                    <Button
                                        responsive
                                        disabled={isSubmitting}
                                        leftIcon={<i className="fa fa-times" />}
                                        onClick={() => navigate(biddersLinks.bidders())}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            }
                        >
                            <form onSubmit={handleSubmit} className={style.inlineEditForm}>
                                <div className={style.summaryGrid}>
                                <div className={style.summaryCard}>
                                    <ResourceBlock
                                        title='General'
                                        className={style.summaryCardBlock}
                                    >
                                        <div className={style.infoColumns}>
                                            <div className={style.inlineFormList}>
                                                <Field
                                                    name='title'
                                                    label='Title'
                                                    required
                                                    className={[style.inlineField, 'bidder-inline-focus-title'].join(' ')}
                                                    options={{
                                                        placeholder: 'Title',
                                                        autoFocus: true,
                                                        disabled: isSubmitting
                                                    }}
                                                    component={InputField}
                                                />
                                                <Field
                                                    name='agencyId'
                                                    label='Agency'
                                                    required
                                                    className={style.inlineField}
                                                    options={{
                                                        placeholder: 'Agency',
                                                        disabled: isSubmitting,
                                                        searchable: true
                                                    }}
                                                    component={SelectAgencyField}
                                                />
                                                <Field
                                                    name='status'
                                                    label='Status'
                                                    className={style.inlineField}
                                                    options={{
                                                        placeholder: 'Status',
                                                        disabled: isSubmitting
                                                    }}
                                                    component={SelectBidderStatusField}
                                                />
                                                <Field
                                                    name='auctionCategory'
                                                    label='Auction Category'
                                                    required
                                                    className={style.inlineField}
                                                    options={{
                                                        placeholder: 'Auction Category',
                                                        disabled: isSubmitting,
                                                        items: [{
                                                            label: "performance",
                                                            value: "performance"
                                                        }, {
                                                            label: 'branded',
                                                            value: 'branded'
                                                        }]
                                                    }}
                                                    component={SelectField}
                                                />
                                                <Field
                                                    name='tags'
                                                    label='Tags'
                                                    className={style.inlineField}
                                                    options={{
                                                        placeholder: 'Tags',
                                                        disabled: isSubmitting,
                                                        dropdown: true
                                                    }}
                                                    component={TagsField}
                                                />
                                                <Field
                                                    name='maxRpm'
                                                    label='Max QPS'
                                                    required
                                                    className={[style.inlineField, 'bidder-inline-focus-maxRpm'].join(' ')}
                                                    type='number'
                                                    options={{
                                                        placeholder: 'Max QPS',
                                                        type: InputType.INTEGER,
                                                        disabled: isSubmitting,
                                                        min: 0,
                                                        step: 1
                                                    }}
                                                    component={InputField}
                                                />
                                                <Field
                                                    name='minBidFloor'
                                                    label='Min Floor'
                                                    className={style.inlineField}
                                                    options={{
                                                        placeholder: 'Min Floor',
                                                        type: InputType.FLOAT,
                                                        disabled: isSubmitting,
                                                        min: 0,
                                                        step: 0.01
                                                    }}
                                                    component={InputField}
                                                />
                                                <Field
                                                    name='maxBidFloor'
                                                    label='Max Floor'
                                                    className={style.inlineField}
                                                    options={{
                                                        placeholder: 'Max Floor',
                                                        type: InputType.FLOAT,
                                                        disabled: isSubmitting,
                                                        min: 0,
                                                        step: 0.01
                                                    }}
                                                    component={InputField}
                                                />
                                                <Field
                                                    name='impExpirationTime'
                                                    label='Expiration time'
                                                    className={style.inlineField}
                                                    type='number'
                                                    options={{
                                                        placeholder: 'Expiration time',
                                                        type: InputType.INTEGER,
                                                        disabled: isSubmitting,
                                                        min: 0,
                                                        step: 1
                                                    }}
                                                    component={InputField}
                                                />
                                                <Field
                                                    name='healthyAvgPrice'
                                                    label='RPM guidance'
                                                    className={style.inlineField}
                                                    options={{
                                                        placeholder: '0.00',
                                                        type: InputType.FLOAT,
                                                        disabled: isSubmitting,
                                                        min: 0,
                                                        step: 0.01
                                                    }}
                                                    component={InputField}
                                                />
                                                <Field
                                                    name='tagIdRequirement'
                                                    label='TagID requirement'
                                                    className={style.inlineField}
                                                    options={{
                                                        placeholder: 'TagID requirement',
                                                        disabled: isSubmitting,
                                                        items: tagIdRequirementItems
                                                    }}
                                                    component={SelectField}
                                                />
                                                <Field
                                                    name='gpidRequirement'
                                                    label='GPID requirement'
                                                    className={style.inlineField}
                                                    options={{
                                                        placeholder: 'GPID requirement',
                                                        disabled: isSubmitting,
                                                        items: gpidRequirementItems
                                                    }}
                                                    component={SelectField}
                                                />
                                            </div>
                                            <div className={style.inlineFormList}>
                                                <Field
                                                    name="useAdMergeType"
                                                    label="Merge Ad Types"
                                                    className={style.inlineCheckboxField}
                                                    component={CheckboxField}
                                                    options={{placeholder: 'Merge Ad Types', disabled: isSubmitting}}
                                                />
                                                <Field
                                                    name='coppaFlag'
                                                    label='Coppa'
                                                    className={style.inlineCheckboxField}
                                                    component={CheckboxField}
                                                    options={{
                                                        placeholder: 'Coppa',
                                                        disabled: isSubmitting
                                                    }}
                                                />
                                                <Field
                                                    name="excludeFromNonInBannerAuction"
                                                    label="Exclude from non in-banner auction"
                                                    className={style.inlineCheckboxField}
                                                    component={CheckboxField}
                                                    options={{
                                                        placeholder: 'Exclude from non in-banner auction',
                                                        disabled: isSubmitting
                                                    }}
                                                />
                                                <Field
                                                    name='adControl'
                                                    label='Ad control'
                                                    className={style.inlineCheckboxField}
                                                    component={CheckboxField}
                                                    options={{
                                                        placeholder: 'Ad control',
                                                        disabled: isSubmitting
                                                    }}
                                                />
                                                <Field
                                                    name='delayedNotification'
                                                    label='Delayed notification'
                                                    className={style.inlineCheckboxField}
                                                    component={CheckboxField}
                                                    options={{
                                                        placeholder: 'Delayed notification',
                                                        disabled: isSubmitting
                                                    }}
                                                />
                                                <Field
                                                    name='clientNurl'
                                                    label='Client NURL'
                                                    className={style.inlineCheckboxField}
                                                    component={CheckboxField}
                                                    options={{
                                                        placeholder: 'Client NURL',
                                                        disabled: isSubmitting
                                                    }}
                                                />
                                                <Field
                                                    name='clientLurl'
                                                    label='Client LURL'
                                                    className={style.inlineCheckboxField}
                                                    component={CheckboxField}
                                                    options={{
                                                        placeholder: 'Client LURL',
                                                        disabled: isSubmitting
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </ResourceBlock>
                                </div>
                                <div className={style.summaryCard}>
                                    <ResourceBlock
                                        title='Integration'
                                        className={style.summaryCardBlock}
                                    >
                                        <div className={style.inlineFormList}>
                                            <Field
                                                name='protocol'
                                                label='Protocol'
                                                className={style.inlineField}
                                                options={{
                                                    placeholder: 'Protocol',
                                                    disabled: isSubmitting,
                                                    searchable: true
                                                }}
                                                component={SelectBidderProtocolField}
                                            />
                                            <Field
                                                name='encoding'
                                                label='Encoding'
                                                className={style.inlineField}
                                                options={{
                                                    placeholder: 'Default by protocol',
                                                    disabled: isSubmitting,
                                                    clearable: true
                                                }}
                                                component={SelectBidderEncodingField}
                                            />
                                            <Field
                                                name='endpoint'
                                                label='Endpoint'
                                                required
                                                className={[style.inlineField, 'bidder-inline-focus-endpoint'].join(' ')}
                                                options={{
                                                    placeholder: 'Endpoint',
                                                    disabled: isSubmitting
                                                }}
                                                component={InputField}
                                            />
                                            <Field
                                                name='dc'
                                                label='Datacenters'
                                                className={style.inlineField}
                                                options={{
                                                    placeholder: 'Datacenters',
                                                    disabled: isSubmitting
                                                }}
                                                component={SelectDcField}
                                            />
                                            <Field
                                                name='wseat'
                                                label='wseat'
                                                className={[style.inlineField, style.inlineMultilineField].join(' ')}
                                                options={{
                                                    mode: 'text',
                                                    disabled: isSubmitting,
                                                    minLines: 6,
                                                    maxLines: 20
                                                }}
                                                component={EditorField}
                                            />
                                            <Field
                                                name='bseat'
                                                label='bseat'
                                                className={[style.inlineField, style.inlineMultilineField].join(' ')}
                                                options={{
                                                    mode: 'text',
                                                    disabled: isSubmitting,
                                                    minLines: 6,
                                                    maxLines: 20
                                                }}
                                                component={EditorField}
                                            />
                                            <Field
                                                name='floorType'
                                                label='Floor type'
                                                required
                                                className={[style.inlineField, 'bidder-inline-focus-floorType'].join(' ')}
                                                options={{
                                                    placeholder: 'No floor',
                                                    disabled: isSubmitting
                                                }}
                                                component={SelectFloorTypeField}
                                            />
                                            <Field
                                                name='skAnIdCheck'
                                                label='Check for SK AN ID'
                                                className={style.inlineCheckboxField}
                                                component={CheckboxField}
                                                options={{
                                                    placeholder: 'Check for SK AN ID',
                                                    disabled: isSubmitting
                                                }}
                                            />
                                            <Field
                                                name='skAnId'
                                                label='SK AN ID'
                                                className={style.inlineField}
                                                options={{
                                                    placeholder: 'SK AN ID',
                                                    disabled: isSubmitting
                                                }}
                                                component={InputField}
                                            />
                                            <Field
                                                name='passCalloutRound'
                                                label='Pass callout round'
                                                className={style.inlineCheckboxField}
                                                component={CheckboxField}
                                                options={{
                                                    placeholder: 'Pass callout round',
                                                    disabled: isSubmitting
                                                }}
                                            />
                                        </div>
                                    </ResourceBlock>
                                    {bidder.protocol === 'mock' && (
                                        <ResourceBlock
                                            title='Mock bid response'
                                            className={style.summaryCardBlock}
                                        >
                                            <div className={style.inlineFormList}>
                                                <Field
                                                    name='mockBidResponse'
                                                    className={style.inlineNoLabelField}
                                                    options={{
                                                        mode: 'json',
                                                        disabled: isSubmitting,
                                                        canFullScreen: true
                                                    }}
                                                    component={EditorField}
                                                />
                                            </div>
                                        </ResourceBlock>
                                    )}
                                </div>
                            </div>
                            <div className={style.summaryGrid}>
                                <div className={style.summaryCard}>
                                    <ResourceBlock
                                        title='Spend Limits'
                                        className={style.summaryCardBlock}
                                    >
                                            <div className={style.inlineFormList}>
                                                <Field
                                                    name='spendCapping.limit'
                                                    label='USD per hour'
                                                    help="Seeds a bidder's spend limits and holds only when no plan is possible. Saving it changes no existing limits."
                                                    className={style.inlineField}
                                                    type='number'
                                                    options={{
                                                        placeholder: 'USD per hour',
                                                        type: InputType.FLOAT,
                                                        disabled: isSubmitting,
                                                        min: 0,
                                                        step: 0.1
                                                    }}
                                                    component={InputField}
                                                />
                                                <Field
                                                    name='spendCapping.test'
                                                    label='Test'
                                                    className={style.inlineCheckboxField}
                                                    component={CheckboxField}
                                                    options={{
                                                        placeholder: 'Test',
                                                        disabled: isSubmitting
                                                    }}
                                                />
                                            </div>
                                    </ResourceBlock>
                                </div>
                            </div>
                            </form>
                        </BreadcrumbsResourcePage>
                        </BidderFormCtx.Provider>
                    );
                }}
            </bidderForm.Subscribe>
        </Breadcrumb>
    );
};

const BidderDetailsView = () => {
    const ability = useAbility(BiddersAbility);
    const preTargetingAbility = useAbility(PreTargetingAbility);
    const confirm = useConfirm();
    const params = useParams<IBidderViewParams>();
    const location = useLocation();
    const navigate = useNavigate();
    const bidderId = params.bidderId;
    const preTargetingEditId = React.useMemo(() => {
        const value = new URLSearchParams(location.search).get('preTargetingEdit');
        const parsed = value ? Number(value) : NaN;
        return Number.isFinite(parsed) ? parsed : null;
    }, [location.search]);
    const handledPreTargetingRef = React.useRef<number | null>(null);
    const bidderResult = useQueryResource(() => fetchBidder(bidderId), [bidderId], null);
    const bidderGroupsResult = useQueryResource(() => fetchBidderGroupsByBidder(bidderId), [bidderId], []);
    const currentHourSpend = useQueryResource(() => fetchCurrentHourSpend(bidderId), [bidderId], null);
    const now = React.useMemo(() => new Date(), []);
    const today = utcDay(0, now);
    const spendLimits = useQueryResource(() => fetchBidderSpendLimits(bidderId, today), [bidderId, today], null);
    const hourlyLimits = spendLimits.resource?.hourly ?? [];
    const scale = spendLimits.resource?.scale;
    const scaleLabel = scale ? `×${round2(scale.factor)} through ${scale.lastDay}` : '—';
    const preTargetings = useDataSource(preTargetingConfigsAdapter);
    const modals = useModals();
    const isPending = bidderResult.isPending;
    const [inlineEditSection, setInlineEditSection] = useState<InlineEditMode | null>(null);

    const isEditingSection = (section: InlineEditSection) =>
        inlineEditSection === section || inlineEditSection === 'all';
    const isInlineEditActive = inlineEditSection !== null;

    const bidderForm = useForm({
        defaultValues: toBidderFormValues(bidderResult.resource ?? defaultBidder),
        validators: {
            onSubmit: createBidderFormSchema(ability.isAdmin()) as any
        },
        onSubmit: async ({value}) => {
            const bidder = toBidderSubmitValues(value);

            const save = async () => {
                try {
                    const updatedBidder = await updateBidder(prepareBidderSubmitData(bidder));
                    bidderResult.setResource(updatedBidder);
                    bidderGroupsResult.fetch();
                    if (isEditingSection('spendCapping')) {
                        currentHourSpend.fetch();
                    }
                    setInlineEditSection(null);
                    notify.success('Saved');
                } catch (error) {
                    notify.apiError(error);
                }
            };

            const originalBidder = bidderResult.resource;
            if (originalBidder && originalBidder.protocol !== 'mock' && bidder.protocol === 'mock') {
                const approved = await new Promise<boolean>(resolve => {
                    confirm('Are you sure you want to save the bidder with mock protocol?', {
                        onOk: () => resolve(true),
                        onNo: () => resolve(false),
                        icon: <i className='fa fa-exclamation' />,
                        intent: Intent.DANGER
                    });
                });
                if (!approved) return;
            }

            await save();
        }
    });

    useEffect(() => {
        if (!bidderId) {
            return;
        }
        preTargetings.fetchRecords(bidderId);
    }, [bidderId]);

    const createPreTargeting = modals.create<IPreTargetingModalProps>(PreTargetingForm, {
        onSubmit: (preTargeting, formActions, close) => {
            preTargetings.createRecord(preTargeting)
                .then(() => {
                    close();
                    notify.success('Pre-targeting config created successfully');
                })
                .catch(error => {
                    formActions.setSubmitting(false);
                    formActions.setStatus(getApiErrorMessage(error));
                });
        }
    });

    const editPreTargeting = modals.create<IPreTargetingModalProps>(PreTargetingForm, {
        onSubmit: (preTargeting, formActions, close) => {
            preTargetings.updateRecord(preTargeting)
                .then(() => {
                    close();
                    notify.success('Pre-targeting config saved successfully');
                })
                .catch(error => {
                    formActions.setSubmitting(false);
                    formActions.setStatus(getApiErrorMessage(error));
                });
        }
    });

    const scaleSpendLimits = modals.create<IScaleSpendLimitsModalProps>(ScaleSpendLimitsModal, {
        onSubmit: ({factor}, formActions, close) => {
            return scaleBidderSpendLimits(bidderId, factor)
                .then(() => {
                    notify.success('Spend limits scaled');
                    close();
                    spendLimits.fetch();
                })
                .catch(error => formActions.setStatus(getApiErrorMessage(error)));
        }
    });

    const replanSpendLimits = modals.create<IReplanSpendLimitsModalProps>(ReplanSpendLimitsModal, {
        title: 'Re-plan spend limits',
        onSubmit: ({date}, formActions, close) => {
            return replanBidderSpendLimits(bidderId, date)
                .then(() => {
                    notify.success('Spend limits re-planned');
                    close();
                    spendLimits.fetch();
                })
                .catch(error => formActions.setStatus(getApiErrorMessage(error)));
        }
    });

    const hourlyLimitsChart = hourlyLimits.length > 0 && (
        <BarChart
            data={hourlyLimits.map((limit, hour) => ({
                timestamp: `${today}T${String(hour).padStart(2, '0')}:00:00Z`,
                spent: limit
            }) as IPerformance)}
            fields={['spent']}
            granularity={Granularity.HOUR}
            formatLabel={date => `${String(date.getUTCHours()).padStart(2, '0')}:00`}
            compact
            highlight={now.getUTCHours()}
            mark={currentHourSpend.resource ? {index: now.getUTCHours(), value: currentHourSpend.resource.spend} : undefined}
        />
    );

    const editRules = modals.create(PreTargetingRulesEditorForm, {});

    useEffect(() => {
        if (preTargetingEditId == null) {
            return;
        }
        if (handledPreTargetingRef.current === preTargetingEditId) {
            return;
        }
        handledPreTargetingRef.current = preTargetingEditId;

        const params = new URLSearchParams(location.search);
        params.delete('preTargetingEdit');
        navigate({search: params.toString()}, {replace: true});

        fetchPreTargetingConfig(preTargetingEditId)
            .then((config) => editPreTargeting.show({preTargeting: config}))
            .catch((error) => {
                notify.apiError(error, 'Failed to load PreTargeting config.');
            });
    }, [preTargetingEditId, location.search, navigate, editPreTargeting]);

    const blockClassName = (section?: InlineEditSection) => {
        const classes = [style.summaryCardBlock];
        if (isInlineEditActive && section) {
            classes.push(style.editingBlock);
        }
        return classes.join(' ');
    };

    return <ResourceLoader result={bidderResult} message='Loading bidder'>
        {({resource: bidder}) => {
            if (!bidder) {
                return (
                    <Breadcrumb breadcrumb={{to: biddersLinks.bidders(), title: 'Bidders'}}>
                        <BreadcrumbsResourcePage title="Bidders">
                            <Title>Bidder not found</Title>
                        </BreadcrumbsResourcePage>
                    </Breadcrumb>
                );
            }

            return <Breadcrumb breadcrumb={{
                to: biddersLinks.bidder(bidder.id),
                title: bidder.title
            }}>
                <bidderForm.Subscribe selector={state => state}>
                    {state => {
                        const setFieldValue = (fieldName: string, fieldValue: unknown) => {
                            bidderForm.setFieldValue(fieldName as never, fieldValue as never);
                        };

                        const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
                            event?.preventDefault();
                            void bidderForm.handleSubmit();
                        };

                        const fieldErrors = getFormBridgeErrors(bidderForm);
                        const formBridge = {
                            errors: fieldErrors,
                            touched: {},
                            submitCount: state.submissionAttempts,
                            setFieldValue,
                            setStatus: (_: unknown) => undefined,
                            setSubmitting: (_submitting: boolean) => undefined,
                        };

                        const values = state.values as BidderFormValues;
                        const isSubmitting = state.isSubmitting;

                        return (
                        <BidderFormCtx.Provider value={{form: bidderForm, formBridge}}>
                        <BreadcrumbsResourcePage
                            title={bidder.title}
                            toolbar={
                                ability.canEdit() && (
                                    isInlineEditActive && inlineEditSection === 'all' ? (
                                        <>
                                            <Button
                                                responsive
                                                intent={Intent.PRIMARY}
                                                disabled={isSubmitting || isPending}
                                                leftIcon={<i className="fa fa-check" />}
                                                onClick={() => void bidderForm.handleSubmit()}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                responsive
                                                disabled={isSubmitting}
                                                leftIcon={<i className="fa fa-times" />}
                                                onClick={() => { setInlineEditSection(null); bidderForm.reset(); }}
                                            >
                                                Cancel
                                            </Button>
                                        </>
                                    ) : !isInlineEditActive && (
                                        <Button
                                            responsive
                                            key="edit"
                                            disabled={isPending}
                                            leftIcon={<i className="fa fa-edit" />}
                                            onClick={() => {
                                                bidderForm.reset();
                                                setInlineEditSection('all');
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    )
                                )
                            }
                        >
                            <form onSubmit={handleSubmit} className={style.inlineEditForm}>
                                <div className={style.summaryGrid}>
                                    <div className={style.summaryCard}>
                                        <ResourceBlock
                                            title='General'
                                            className={blockClassName('general')}

                                        >
                                            {isEditingSection('general') ? (
                                                <div className={style.infoColumns}>
                                                    <div className={style.inlineFormList}>
                                                        <Field
                                                            name='title'
                                                            label='Title'
                                                            required
                                                            className={[style.inlineField, 'bidder-inline-focus-title'].join(' ')}
                                                            options={{
                                                                placeholder: 'Title',
                                                                disabled: isSubmitting
                                                            }}
                                                            component={InputField}
                                                        />
                                                        <Field
                                                            name='status'
                                                            label='Status'
                                                            className={style.inlineField}
                                                            options={{
                                                                placeholder: 'Status',
                                                                disabled: isSubmitting
                                                            }}
                                                            component={SelectBidderStatusField}
                                                        />
                                                        <Field
                                                            name='auctionCategory'
                                                            label='Auction Category'
                                                            required
                                                            className={style.inlineField}
                                                            options={{
                                                                placeholder: 'Auction Category',
                                                                disabled: isSubmitting,
                                                                items: [{
                                                                    label: "performance",
                                                                    value: "performance"
                                                                }, {
                                                                    label: 'branded',
                                                                    value: 'branded'
                                                                }]
                                                            }}
                                                            component={SelectField}
                                                            disabled={isSubmitting}
                                                        />
                                                        <Field
                                                            name='tags'
                                                            label='Tags'
                                                            className={style.inlineField}
                                                            options={{
                                                                placeholder: 'Tags',
                                                                disabled: isSubmitting,
                                                                dropdown: true
                                                            }}
                                                            component={TagsField}
                                                        />
                                                        <Field
                                                            name='maxRpm'
                                                            label='Max QPS'
                                                            required
                                                            className={[style.inlineField, 'bidder-inline-focus-maxRpm'].join(' ')}
                                                            type='number'
                                                            options={{
                                                                placeholder: 'Max QPS',
                                                                type: InputType.INTEGER,
                                                                disabled: isSubmitting,
                                                                min: 0,
                                                                step: 1
                                                            }}
                                                            component={InputField}
                                                        />
                                                        <Field
                                                            name='minBidFloor'
                                                            label='Min Floor'
                                                            className={style.inlineField}
                                                            options={{
                                                                placeholder: 'Min Floor',
                                                                type: InputType.FLOAT,
                                                                disabled: isSubmitting,
                                                                min: 0,
                                                                step: 0.01
                                                            }}
                                                            component={InputField}
                                                        />
                                                        <Field
                                                            name='maxBidFloor'
                                                            label='Max Floor'
                                                            className={style.inlineField}
                                                            options={{
                                                                placeholder: 'Max Floor',
                                                                type: InputType.FLOAT,
                                                                disabled: isSubmitting,
                                                                min: 0,
                                                                step: 0.01
                                                            }}
                                                            component={InputField}
                                                        />
                                                        <Field
                                                            name='impExpirationTime'
                                                            label='Expiration time'
                                                            className={style.inlineField}
                                                            type='number'
                                                            options={{
                                                                placeholder: 'Expiration time',
                                                                type: InputType.INTEGER,
                                                                disabled: isSubmitting,
                                                                min: 0,
                                                                step: 1
                                                            }}
                                                            component={InputField}
                                                        />
                                                        <Field
                                                            name='healthyAvgPrice'
                                                            label='RPM guidance'
                                                            className={style.inlineField}
                                                            options={{
                                                                placeholder: '0.00',
                                                                type: InputType.FLOAT,
                                                                disabled: isSubmitting,
                                                                min: 0,
                                                                step: 0.01
                                                            }}
                                                            component={InputField}
                                                        />
                                                        <Field
                                                            name='tagIdRequirement'
                                                            label='TagID requirement'
                                                            className={style.inlineField}
                                                            options={{
                                                                placeholder: 'TagID requirement',
                                                                disabled: isSubmitting,
                                                                items: tagIdRequirementItems
                                                            }}
                                                            component={SelectField}
                                                        />
                                                        <Field
                                                            name='gpidRequirement'
                                                            label='GPID requirement'
                                                            className={style.inlineField}
                                                            options={{
                                                                placeholder: 'GPID requirement',
                                                                disabled: isSubmitting,
                                                                items: gpidRequirementItems
                                                            }}
                                                            component={SelectField}
                                                        />
                                                    </div>
                                                    <div className={style.inlineFormList}>
                                                        <Field
                                                            name="useAdMergeType"
                                                            label="Merge Ad Types"
                                                            className={style.inlineCheckboxField}
                                                            component={CheckboxField}
                                                            options={{placeholder: 'Merge Ad Types', disabled: isSubmitting}}
                                                        />
                                                        <Field
                                                            name='coppaFlag'
                                                            label='Coppa'
                                                            className={style.inlineCheckboxField}
                                                            component={CheckboxField}
                                                            options={{
                                                                placeholder: 'Coppa',
                                                                disabled: isSubmitting
                                                            }}
                                                        />
                                                        <Field
                                                            name="excludeFromNonInBannerAuction"
                                                            label="Exclude from non in-banner auction"
                                                            className={style.inlineCheckboxField}
                                                            component={CheckboxField}
                                                            options={{
                                                                placeholder: 'Exclude from non in-banner auction',
                                                                disabled: isSubmitting
                                                            }}
                                                        />
                                                        <Field
                                                            name='adControl'
                                                            label='Ad control'
                                                            className={style.inlineCheckboxField}
                                                            component={CheckboxField}
                                                            options={{
                                                                placeholder: 'Ad control',
                                                                disabled: isSubmitting
                                                            }}
                                                        />
                                                        <Field
                                                            name='delayedNotification'
                                                            label='Delayed notification'
                                                            className={style.inlineCheckboxField}
                                                            component={CheckboxField}
                                                            options={{
                                                                placeholder: 'Delayed notification',
                                                                disabled: isSubmitting
                                                            }}
                                                        />
                                                        <Field
                                                            name='clientNurl'
                                                            label='Client NURL'
                                                            className={style.inlineCheckboxField}
                                                            component={CheckboxField}
                                                            options={{
                                                                placeholder: 'Client NURL',
                                                                disabled: isSubmitting
                                                            }}
                                                        />
                                                        <Field
                                                            name='clientLurl'
                                                            label='Client LURL'
                                                            className={style.inlineCheckboxField}
                                                            component={CheckboxField}
                                                            options={{
                                                                placeholder: 'Client LURL',
                                                                disabled: isSubmitting
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className={style.infoColumns}>
                                                    <div className={style.infoList}>
                                                        {renderInfoFields([
                                                            {label: 'Name', value: bidder.title},
                                                            {
                                                                label: 'Agency',
                                                                value: typeof bidder.agencyId === 'number'
                                                                    ? <NavLink to={clientsLinks.agency(bidder.agencyId)}>{bidder.agencyId}</NavLink>
                                                                    : '—'
                                                            },
                                                            {
                                                                label: 'Status',
                                                                value: (
                                                                    <span>
                                                                        {bidder.status}
                                                                        {!!bidder.pauseReasons?.length && (
                                                                            <InfoPopover
                                                                                ariaLabel="Reason"
                                                                                content={`Paused: ${bidder.pauseReasons.map(humanizePauseReason).join(', ')}${bidder.pausedUntil ? ` — until ${formatDateTime(bidder.pausedUntil)}` : ''}`}
                                                                            />
                                                                        )}
                                                                    </span>
                                                                )
                                                            },
                                                            {label: 'Auction Category', value: bidder.auctionCategory},
                                                            {
                                                                label: 'Tags',
                                                                value: bidder.tags && bidder.tags.length
                                                                    ? <TagsList values={bidder.tags} size="small"/>
                                                                    : '—'
                                                            },
                                                            {label: 'Max QPS', value: bidder.maxRpm},
                                                            {label: 'Min Floor', value: bidder.minBidFloor},
                                                            {label: 'Max Floor', value: bidder.maxBidFloor},
                                                            {label: 'Expiration time', value: bidder.impExpirationTime},
                                                            {label: 'RPM guidance', value: bidder.healthyAvgPrice ?? '—'}
                                                        ])}
                                                    </div>
                                                    <div className={style.infoList}>
                                                        {renderInfoFields([
                                                            {label: 'Coppa', value: renderBoolean(bidder.coppaFlag)},
                                                            {label: 'Exclude from non in-banner auction', value: renderBoolean(bidder.excludeFromNonInBannerAuction)},
                                                            {label: 'Delayed notification', value: renderBoolean(bidder.delayedNotification)},
                                                            {label: 'Ad control', value: renderBoolean(bidder.adControl)},
                                                            {label: 'Merge Ad Types', value: renderBoolean(!!bidder.useAdMergeType)},
                                                            {label: 'Client NURL', value: renderBoolean(bidder.clientNurl)},
                                                            {label: 'Client LURL', value: renderBoolean(bidder.clientLurl)},
                                                            {label: 'TagID requirement', value: tagIdRequirementLabels[bidder.tagIdRequirement]},
                                                            {label: 'GPID requirement', value: gpidRequirementLabels[bidder.gpidRequirement]}
                                                        ])}
                                                    </div>
                                                </div>
                                            )}
                                        </ResourceBlock>
                                    </div>
                                    <div className={style.summaryCard}>
                                        <ResourceBlock
                                            title='Integration'
                                            className={blockClassName('integration')}
                                        >
                                            {isEditingSection('integration') ? (
                                                <div className={style.inlineFormList}>
                                                    <Field
                                                        name='protocol'
                                                        label='Protocol'
                                                        className={style.inlineField}
                                                        options={{
                                                            placeholder: 'Protocol',
                                                            disabled: isSubmitting,
                                                            searchable: true
                                                        }}
                                                        component={SelectBidderProtocolField}
                                                    />
                                                    <Field
                                                        name='encoding'
                                                        label='Encoding'
                                                        className={style.inlineField}
                                                        options={{
                                                            placeholder: 'Default by protocol',
                                                            disabled: isSubmitting,
                                                            clearable: true
                                                        }}
                                                        component={SelectBidderEncodingField}
                                                    />
                                                    <Field
                                                        name='endpoint'
                                                        label='Endpoint'
                                                        required
                                                        className={[style.inlineField, 'bidder-inline-focus-endpoint'].join(' ')}
                                                        options={{
                                                            placeholder: 'Endpoint',
                                                            disabled: isSubmitting
                                                        }}
                                                        component={InputField}
                                                    />
                                                    <Field
                                                        name='dc'
                                                        label='Datacenters'
                                                        className={style.inlineField}
                                                        options={{
                                                            placeholder: 'Datacenters',
                                                            disabled: isSubmitting
                                                        }}
                                                        component={SelectDcField}
                                                    />
                                                    <Field
                                                        name='wseat'
                                                        label='wseat'
                                                        className={[style.inlineField, style.inlineMultilineField].join(' ')}
                                                        options={{
                                                            mode: 'text',
                                                            disabled: isSubmitting,
                                                            minLines: 6,
                                                            maxLines: 20
                                                        }}
                                                        component={EditorField}
                                                    />
                                                    <Field
                                                        name='bseat'
                                                        label='bseat'
                                                        className={[style.inlineField, style.inlineMultilineField].join(' ')}
                                                        options={{
                                                            mode: 'text',
                                                            disabled: isSubmitting,
                                                            minLines: 6,
                                                            maxLines: 20
                                                        }}
                                                        component={EditorField}
                                                    />
                                                    <Field
                                                        name='floorType'
                                                        label='Floor type'
                                                        required
                                                        className={[style.inlineField, 'bidder-inline-focus-floorType'].join(' ')}
                                                        options={{
                                                            placeholder: 'No floor',
                                                            disabled: isSubmitting
                                                        }}
                                                        component={SelectFloorTypeField}
                                                    />
                                                    <Field
                                                        name='skAnIdCheck'
                                                        label='Check for SK AN ID'
                                                        className={style.inlineCheckboxField}
                                                        component={CheckboxField}
                                                        options={{
                                                            placeholder: 'Check for SK AN ID',
                                                            disabled: isSubmitting
                                                        }}
                                                    />
                                                    <Field
                                                        name='skAnId'
                                                        label='SK AN ID'
                                                        className={style.inlineField}
                                                        options={{
                                                            placeholder: 'SK AN ID',
                                                            disabled: isSubmitting
                                                        }}
                                                        component={InputField}
                                                    />
                                                    <Field
                                                        name='passCalloutRound'
                                                        label='Pass Callout Round'
                                                        className={style.inlineCheckboxField}
                                                        component={CheckboxField}
                                                        options={{
                                                            placeholder: 'Pass Callout Round',
                                                            disabled: isSubmitting
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className={style.infoList}>
                                                    {renderInfoFields([
                                                        {label: 'Protocol', value: bidder.protocol},
                                                        {label: 'Encoding', value: bidder.encoding ?? '—'},
                                                        {
                                                            label: 'Endpoint',
                                                            value: bidder.endpoint
                                                                ? <Copy text={bidder.endpoint}>{bidder.endpoint}</Copy>
                                                                : '—'
                                                        },
                                                        {
                                                            label: 'Datacenters',
                                                            value: bidder.dc && bidder.dc.length
                                                                ? <TagsList values={bidder.dc} size="small"/>
                                                                : '—'
                                                        },
                                                        {
                                                            label: 'wseat',
                                                            value: tagListData(bidder.wseat).length
                                                                ? <div className={style.seatValue}>
                                                                    <Copy text={tagListData(bidder.wseat).join('\n')}/>
                                                                    <span>{tagListData(bidder.wseat).join(', ')}</span>
                                                                </div>
                                                                : '—'
                                                        },
                                                        {
                                                            label: 'bseat',
                                                            value: tagListData(bidder.bseat).length
                                                                ? <div className={style.seatValue}>
                                                                    <Copy text={tagListData(bidder.bseat).join('\n')}/>
                                                                    <span>{tagListData(bidder.bseat).join(', ')}</span>
                                                                </div>
                                                                : '—'
                                                        },
                                                        {label: 'Floor type', value: bidder.floorType},
                                                        {label: 'Check for SK AN ID', value: renderBoolean(bidder.skAnIdCheck)},
                                                        {label: 'SK AN ID', value: bidder.skAnId},
                                                        {label: 'Pass callout round', value: renderBoolean(bidder.passCalloutRound)},
                                                        {
                                                            label: 'Mock bid response',
                                                            value: bidder.mockBidResponse
                                                                ? <Copy text={bidder.mockBidResponse}/>
                                                                : '—'
                                                        }
                                                    ])}
                                                </div>
                                            )}
                                        </ResourceBlock>
                                        {isEditingSection('integration') && values.protocol === 'mock' && (
                                            <ResourceBlock
                                                title='Mock bid response'
                                                className={blockClassName('mock')}
                                            >
                                                <div className={style.inlineFormList}>
                                                    <Field
                                                        name='mockBidResponse'
                                                        className={style.inlineNoLabelField}
                                                        options={{
                                                            mode: 'json',
                                                            disabled: isSubmitting,
                                                            canFullScreen: true
                                                        }}
                                                        component={EditorField}
                                                    />
                                                </div>
                                            </ResourceBlock>
                                        )}
                                    </div>
                                </div>
                                <div className={style.summaryGrid}>
                                    <div className={style.summaryCard}>
                                        <ResourceBlock title='Groups' className={blockClassName()}>
                                            <div className={style.infoList}>
                                                {renderInfoFields([{
                                                    label: 'Groups',
                                                    value: bidderGroupsResult.resource?.length ? <LinksList values={bidderGroupsResult.resource.map(group => group.name)}
                                                        maxItems={4}
                                                        buildTo={name => {
                                                            const group = bidderGroupsResult.resource?.find(g => g.name === name);
                                                            return group ? biddersLinks.bidderGroup(group.id) : '#';
                                                        }}
                                                    /> : '—'
                                                }])}
                                            </div>
                                        </ResourceBlock>
                                    </div>
                                    <div className={style.summaryCard}>
                                        <ResourceBlock
                                            title='Spend Limits'
                                            className={blockClassName('spendCapping')}
                                        >
                                            {isEditingSection('spendCapping') ? (
                                                    <div className={style.inlineFormList}>
                                                        <Field
                                                            name='spendCapping.limit'
                                                            label='USD per hour'
                                                            help="Seeds a bidder's spend limits and holds only when no plan is possible. Saving it changes no existing limits."
                                                            className={style.inlineField}
                                                            type='number'
                                                            options={{
                                                                placeholder: 'USD per hour',
                                                                type: InputType.FLOAT,
                                                                disabled: isSubmitting,
                                                                min: 0,
                                                                step: 0.1
                                                            }}
                                                            component={InputField}
                                                        />
                                                        <Field
                                                            name='spendCapping.test'
                                                            label='Test'
                                                            className={style.inlineCheckboxField}
                                                            component={CheckboxField}
                                                            options={{
                                                                placeholder: 'Test',
                                                                disabled: isSubmitting
                                                            }}
                                                        />
                                                        <div className={style.inlineField}>
                                                            <label>Hourly limits</label>
                                                            <div className={style.inlineButtons}>
                                                                <Button type='button'
                                                                    disabled={isSubmitting}
                                                                    leftIcon={<i className='fas fa-arrows-alt-v'/>}
                                                                    onClick={() => scaleSpendLimits.show()}
                                                                >Scale…</Button>
                                                                <Button type='button'
                                                                    disabled={isSubmitting}
                                                                    leftIcon={<i className='fas fa-calendar-alt'/>}
                                                                    onClick={() => replanSpendLimits.show()}
                                                                >Re-plan…</Button>
                                                            </div>
                                                        </div>
                                                        <div className={style.inlineField}>
                                                            <label>Scale</label>
                                                            <div className={style.infoValue}>{scaleLabel}</div>
                                                        </div>
                                                        {hourlyLimitsChart}
                                                    </div>
                                            ) : (
                                                <div className={style.infoList}>
                                                    {renderInfoFields([
                                                        {
                                                            label: 'Fallback cap',
                                                            value: `$${bidder.spendCapping.limit} per hour, held only when no plan is possible`
                                                        },
                                                        {
                                                            label: 'Spend this hour',
                                                            value: `$${round2(currentHourSpend.resource?.spend) || 0}`
                                                        },
                                                        {
                                                            label: 'Test',
                                                            value: renderBoolean(bidder.spendCapping.test)
                                                        },
                                                        {
                                                            label: 'Scale',
                                                            value: scaleLabel
                                                        },
                                                        {
                                                            label: 'Hourly limits',
                                                            value: hourlyLimits.length ? `${today} (UTC)` : '—'
                                                        }
                                                    ])}
                                                    {hourlyLimitsChart}
                                                </div>
                                            )}
                                        </ResourceBlock>
                                    </div>
                                </div>
                            </form>
                            {ability.canViewPreTargeting() && <>
                                <div className="layout-content-box">
                                <Title toolbar={<>
                                    {preTargetingAbility.canCreate() && <Button intent={Intent.PRIMARY}
                                        responsive
                                        disabled={!bidder || preTargetings.state.isPending}
                                        onClick={() => createPreTargeting.show({
                                            preTargeting: {
                                                ...defaultPreTargeting,
                                                bidderId: bidder.id
                                            }
                                        })}
                                        leftIcon={<i className='fa fa-plus'/>}
                                    >Create</Button>}
                                    <Button intent={Intent.SUCCESS} responsive
                                        key='refresh'
                                        disabled={preTargetings.state.isPending}
                                        leftIcon={<i className='fa fa-refresh'/>}
                                        onClick={() => preTargetings.fetchRecords(bidder.id)}>
                                        Refresh</Button>
                                </>}>PreTargeting Configs</Title>
                                <TableSort header
                                    columns={[
                                        {header: 'ID', small: true, field: 'id'},
                                        {
                                            field: 'title',
                                            header: 'Title',
                                            sort: sortByString,
                                            cell: preTargeting => <NavLink
                                                to={biddersLinks.bidderPreTargetingConfig(bidder.id, preTargeting.id)}>
                                                {preTargeting.title}
                                            </NavLink>
                                        },
                                        {field: 'priority', header: 'Priority', sort: sortByNumber},
                                        {
                                            field: 'allowCache',
                                            header: 'Allow Cache',
                                            cell: preTargeting => renderBoolean(preTargeting.allowCache),
                                            sort: sortByBoolean
                                        },
                                        {field: 'allowCloseDelay', header: 'Allow close delay', sort: sortByNumber},
                                        {
                                            field: 'active',
                                            header: 'Active',
                                            cell: preTargeting =>
                                                renderToggle(preTargeting.active, value =>
                                                    confirm(preTargeting.active ? 'Deactivate config?' : 'Activate config?', {
                                                        onOk: () => preTargetings.updateRecord({
                                                            ...preTargeting,
                                                            active: value
                                                        }).catch((error) => notify.apiError(error, 'Can\'t activate config.')
                                                        ), icon: null, intent: Intent.WARNING
                                                    })),
                                            sort: sortByBoolean
                                        },
                                        {
                                            header: 'Rules',
                                            small: true,
                                            cell: preTargeting =>
                                                preTargetingAbility.canEdit(preTargeting) ? (
                                                    <Button
                                                        key='rules'
                                                        small
                                                        intent={Intent.WARNING}
                                                        leftIcon={<i className='fa fa-codepen'/>}
                                                        onClick={() => editRules.show({preTargetingId: preTargeting.id, preTargetingTitle: preTargeting.title})}>
                                                        Rules
                                                    </Button>
                                                ) : null
                                        },
                                        {
                                            small: true,
                                            cell: preTargeting => (
                                                <TableRowActions>
                                                    {preTargetingAbility.canEdit(preTargeting) && (
                                                        <>
                                                            <Button
                                                                small
                                                                variant="menu"
                                                                leftIcon={<i className='fa fa-edit'/>}
                                                                onClick={() => editPreTargeting.show({preTargeting})}
                                                            >Edit</Button>
                                                        </>
                                                    )}
                                                    {preTargetingAbility.canDelete(preTargeting) && (
                                                        <Button
                                                            small
                                                            variant="menu"
                                                            leftIcon={<i className='fa fa-trash'/>}
                                                            intent={Intent.DANGER}
                                                            onClick={() => confirm('Delete config?', {
                                                                onOk: () => preTargetings.deleteRecord(preTargeting),
                                                                intent: Intent.DANGER,
                                                                icon: <LucideIcon icon={Trash2} size={20}/>
                                                            })}
                                                        >Delete</Button>
                                                    )}
                                                </TableRowActions>
                                            )
                                        }
                                    ]}
                                    records={preTargetings.state.records}
                                    placeholder={preTargetings.state.isPending && <Progress/>}/>
                                </div>
                            </>}
                        </BreadcrumbsResourcePage>
                        </BidderFormCtx.Provider>
                        );
                    }}
                </bidderForm.Subscribe>
            </Breadcrumb>;
        }}
    </ResourceLoader>;
};

export const BidderView = ({create}: {create?: boolean} = {}) => {
    if (create) {
        return <BidderCreateView />;
    }
    return <BidderDetailsView />;
};
